import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import uuid from 'uuid/v4';
import PropTypes from 'prop-types';
import { getUnixTime, set, getDate, getMonth, getYear } from 'date-fns';
import { useQuery } from '@apollo/client';
import { DragDropContext } from 'react-beautiful-dnd';

import { getTimezoneFromBrowser } from '~/utils/timezone';
import { postsReceived } from '../../../post/slice';
import usePostLimit from '../hooks/usePostLimit';
import useDropPostInMonth from '../hooks/useDropPostInMonth';
import { useMonthlyDates } from '../hooks/useMonthlyDates';
import { actions as calendarActions } from '../../reducer';
import GET_CALENDAR_MONTH_QUERY from '../graphql/month';
import dayCells from '../Days';
import { isDraft as checkIsDraft } from '../../utils/isDraft';

const Row = styled.tr`
  // We are adding 1 to the calculation to account for the header
  // this theoretically will set the height to be smaller than needed but then
  // the css will grow the elements proportionally to fill the space
  height: calc(100% / (${(props) => props.numberWeeks} + 1));
`;

const getNewTimestamp = (fromTimestamp, toTimestamp) => {
  const fromDate = new Date(fromTimestamp * 1000);
  const newDate = set(fromDate, {
    date: getDate(toTimestamp),
    month: getMonth(toTimestamp),
    year: getYear(toTimestamp),
  });

  return getUnixTime(newDate);
};

const findPostById = (posts = [], postId) =>
  posts.find((post) => post.id === postId);

const onDragEnd = ({ result, posts = [], updatePostDueAt }) => {
  const { destination, source, draggableId: postId } = result;

  if (!destination) {
    return;
  }

  // If we drop in the same day cell
  // We don't compare the index here because we check against the timestamp
  if (destination.droppableId === source.droppableId) {
    return;
  }

  const post = findPostById(posts, postId);
  const timestamp = getNewTimestamp(
    post.dueAt,
    Number(destination.droppableId)
  );

  updatePostDueAt({ post, timestamp });
};

const DaysOfMonth = ({
  currentDate,
  channelsSelected,
  hasTwentyFourHourTimeFormat,
  isViewOnly,
  onDropPostSuccess,
  onDropPostError,
  setOpenModal,
}) => {
  const dispatch = useDispatch();
  const cellsPerRow = 7;
  const {
    monthStart,
    calendarStart,
    calendarEnd,
    numberOfWeeks,
    weekStartsOn,
  } = useMonthlyDates(currentDate);

  const variables = {
    startDate: getUnixTime(calendarStart),
    endDate: getUnixTime(calendarEnd),
    seed: 10,
    channelIds: channelsSelected,
    timezone: getTimezoneFromBrowser(),
    use24HourTime: hasTwentyFourHourTimeFormat,
    includeDrafts: true,
  };

  const { data, loading, refetch } = useQuery(GET_CALENDAR_MONTH_QUERY, {
    variables,
    onCompleted: ({ calendar }) => {
      dispatch(postsReceived(calendar?.posts || []));
    },
    notifyOnNetworkStatusChange: true,
  });

  const { updatePostDueAt } = useDropPostInMonth({
    onSuccess: (dropResult) => {
      const newDate = dropResult.updatePostDueAt.post.dueAt;
      const isDraft = checkIsDraft(dropResult.updatePostDueAt.post.type);
      onDropPostSuccess({ newDate, isDraft });
    },
    onError: onDropPostError,
    variables,
  });

  const shouldRefetch = useSelector((state) => state.calendar.shouldRefetch);
  useEffect(() => {
    if (shouldRefetch && channelsSelected.length > 0) {
      refetch();
      dispatch(calendarActions.clearShouldRefetch());
    }
  }, [shouldRefetch, JSON.stringify(channelsSelected)]);

  const postLimit = usePostLimit(numberOfWeeks, currentDate);
  const posts = data?.calendar?.posts;

  return (
    <DragDropContext
      onDragEnd={(dragResult) =>
        onDragEnd({ result: dragResult, updatePostDueAt, posts })
      }
    >
      {dayCells({
        startDate: calendarStart,
        endDate: calendarEnd,
        monthStart,
        weekStartsOn,
        data,
        loading,
        postLimit,
        isViewOnly,
        setOpenModal,
      })
        .reduce((row, cell, index) => {
          // create each row array with 7 cells:
          if (index % cellsPerRow === 0) row.push([]);
          row[row.length - 1].push(cell);
          return row;
        }, [])
        .map((rowContent) => {
          // surround every row with 'tr'
          return (
            <Row
              key={uuid()}
              data-testid="monthly-row"
              numberWeeks={numberOfWeeks}
            >
              {rowContent}
            </Row>
          );
        })}
    </DragDropContext>
  );
};

DaysOfMonth.propTypes = {
  isViewOnly: PropTypes.bool,
  hasTwentyFourHourTimeFormat: PropTypes.bool.isRequired,
  currentDate: PropTypes.instanceOf(Date).isRequired,
  onDropPostSuccess: PropTypes.func.isRequired,
  onDropPostError: PropTypes.func.isRequired,
  channelsSelected: PropTypes.arrayOf(PropTypes.string),
  setOpenModal: PropTypes.func,
};

DaysOfMonth.defaultProps = {
  isViewOnly: true,
  channelsSelected: [],
  setOpenModal: null,
};

export default DaysOfMonth;
